import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, Grid2, LinearProgress, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { AddLocation, Assistant } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSelector } from "../../app/helpers";
import { ChosenTimesWarnings } from "../../features/Checkin/components/chosen-times-warnings.component";
import { LocationUsage } from "../../features/Reports/typings/reports.types";
import { quickAutomatedPlaceBooking } from "../../utils/axios-requests";
import { checkEndBeforeStart, checkStartInPast } from "../../utils/utilities";
import { TimePickers } from "../Pickers/time-pickers.component";
import { RootState } from "../../app/rootReducer";
import { TopListLocationMedia } from "./top-list-location-media.partial";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { initBookingTime } from "../BookingForm/functions/date-time.functions";
import {
  dashboardInteractiveFocusSx,
  dashboardMetricTypographySx,
  topListBookNowRowSx,
  topListCardContainerSx,
  topListHeroContentSx,
  topListHeroTitleSx
} from "../../features/Dashboard/dashboard.styles";
import { DialogHeaderWithClose } from "../Dialog/DialogHeaderWithClose.component";
import { SeeditDialogActions } from "../Dialog/SeeditDialogActions.component";
import { SeeditDialogContent } from "../Dialog/SeeditDialogContent.component";

type P = {
  value: LocationUsage;
  setIsQuickAutomatedPlaceBooking: (q: boolean) => void;
  displayBookNowButton?: boolean;
  bookButtonLabel?: string;
  bookButtonHideIcon?: boolean;
  bookButtonVariant?: "text" | "contained";
  bookButtonColor?: "primary" | "secondary" | "inherit";
  bookButtonSx?: SxProps<Theme>;
};

type BookNowButtonProps = {
  locationName: string;
  onOpenDialog: () => void;
  show: boolean;
  label?: string;
  hideIcon?: boolean;
  variant?: "text" | "contained";
  color?: "primary" | "secondary" | "inherit";
  sx?: SxProps<Theme>;
};

const BookNowButton: React.FC<BookNowButtonProps> = ({
  locationName,
  onOpenDialog,
  show,
  label,
  hideIcon = false,
  variant = "text",
  color = "inherit",
  sx
}) => {
  const { t } = useTranslation();
  const buttonLabel = label ?? t("Book now");
  const endIcon = hideIcon ? undefined : <AddLocation />;
  const buttonSx = (
    sx ? [dashboardInteractiveFocusSx, sx] : dashboardInteractiveFocusSx
  ) as SxProps<Theme>;

  if (!show) {
    return null;
  }

  if (locationName === "No Booking Time") {
    return (
      <Link style={{ textDecoration: "none" }} to={"/booking/0"}>
        <Button
          style={{ textDecoration: "none" }}
          size="small"
          variant={variant}
          color={color}
          sx={buttonSx}
          endIcon={endIcon}
          data-testid="no-location-book-btn"
        >
          {buttonLabel}
        </Button>
      </Link>
    );
  }

  return (
    <Button
      onClick={onOpenDialog}
      sx={buttonSx}
      size="small"
      variant={variant}
      color={color}
      endIcon={endIcon}
      data-testid="location-book-btn"
    >
      {buttonLabel}
    </Button>
  );
};

/**
 * Shows A list of top locations, with location picture, and usage graph.
 * @param props.value Information like usage duration, location picture and name.;
 */
export const TopList: React.FC<P> = ({
  value: { locationName, picturePath, relativeDuration, locationInventoryId, isMobileWorking },
  setIsQuickAutomatedPlaceBooking,
  displayBookNowButton = true,
  bookButtonLabel,
  bookButtonHideIcon = false,
  bookButtonVariant = "text",
  bookButtonColor = "inherit",
  bookButtonSx
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    settings: { standardStartTime, standardEndTime },
    userInformation: {
      sub,
      company: { meta }
    }
  } = useSelector((state: RootState) => state.login);
  const { enqueueSnackbar } = useSnackbar();

  const [chosenTimes, setChosenTimes] = useState({
    startTime: standardStartTime,
    endTime: standardEndTime
  });

  const isTodayAWeekday = DateTime.now().weekday !== 6 && DateTime.now().weekday !== 7;
  const isBookingAllowedForSaturday =
    meta.bookingsOnSaturdaysAllowed && DateTime.now().weekday === 6;
  const isBookingAllowedForSunday = meta.bookingsOnSundaysAllowed && DateTime.now().weekday === 7;
  const checkBookingAllowedDay =
    isTodayAWeekday || isBookingAllowedForSaturday || isBookingAllowedForSunday;
  const showBookNowButton = Boolean(checkBookingAllowedDay && (displayBookNowButton ?? true));

  const handleBooking = () => {
    setLoading(true);
    quickAutomatedPlaceBooking({
      users: [
        {
          userId: sub,
          isExternal: false,
          isMobileWorking: isMobileWorking
        }
      ],
      bookingType: BookingType.WORKPLACE,
      endDate: DateTime.now().toFormat("yyyy-MM-dd"),
      startDate: DateTime.now().toFormat("yyyy-MM-dd"),
      endTime: chosenTimes.endTime,
      startTime: chosenTimes.startTime,
      locationInventoryId
    })
      .then(() => {
        enqueueSnackbar(t("Successfully booked the workplace."), { variant: "success" });
        setIsQuickAutomatedPlaceBooking(true);
        setOpen(false);
        setLoading(false);
      })
      .catch(() => {
        enqueueSnackbar(t("There was a booking error"), { variant: "error" });
        setOpen(false);
        setLoading(false);
      });
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const [initStartTime, initEndTime] = initBookingTime(standardStartTime, standardEndTime);
    setChosenTimes({ startTime: initStartTime, endTime: initEndTime });
  }, [standardEndTime, standardStartTime]);

  return (
    <>
      <Grid2
        data-testid="top-list-container"
        container
        direction="column"
        sx={topListCardContainerSx}
      >
        <TopListLocationMedia locationName={locationName} picturePath={picturePath} />
        <Box sx={topListHeroContentSx}>
          <Typography sx={topListHeroTitleSx} variant="body1">
            {t(locationName)}
          </Typography>
          <Box sx={topListBookNowRowSx}>
            <BookNowButton
              locationName={locationName}
              onOpenDialog={() => setOpen(true)}
              show={showBookNowButton}
              label={bookButtonLabel}
              hideIcon={bookButtonHideIcon}
              variant={bookButtonVariant}
              color={bookButtonColor}
              sx={bookButtonSx}
            />
          </Box>
          {meta.showBookingStatistics && (
            <>
              <Grid2
                sx={{ marginBottom: "10px" }}
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={dashboardMetricTypographySx} variant="h5">
                  {(relativeDuration * 100).toFixed(0)}%
                </Typography>
                <Typography variant="caption">{t("last 30 days")}</Typography>
              </Grid2>
              <LinearProgress
                sx={{ borderRadius: 10, height: 5 }}
                variant="determinate"
                value={relativeDuration * 100}
                color="warning"
              />
            </>
          )}
        </Box>
      </Grid2>

      <Dialog
        onClose={handleClose}
        aria-labelledby="simple-dialog-title"
        open={open}
        fullWidth
        maxWidth="xs"
      >
        <DialogHeaderWithClose
          title={t(locationName)}
          onClose={handleClose}
          titleId="simple-dialog-title"
        />

        <SeeditDialogContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 2 }}>
            <Assistant sx={{ fontSize: "1rem", mt: 0.25, flexShrink: 0 }} />
            <Typography variant="body2">{t("_freeWorkplaceChosen")}</Typography>
          </Box>

          <TimePickers
            chosenTimes={chosenTimes}
            setChosenTimes={setChosenTimes}
            editable={{ start: true, end: true }}
          />
          <ChosenTimesWarnings chosenTimes={chosenTimes} />
        </SeeditDialogContent>

        <SeeditDialogActions>
          <Button
            data-testid="location-book-now-btn"
            disabled={
              checkEndBeforeStart(
                DateTime.now().toFormat("yyyy-MM-dd").valueOf(),
                chosenTimes.startTime,
                DateTime.now().toFormat("yyyy-MM-dd").valueOf(),
                chosenTimes.endTime
              ) ||
              checkStartInPast(
                DateTime.now().toFormat("yyyy-MM-dd").valueOf(),
                chosenTimes.startTime
              ) ||
              loading
            }
            onClick={handleBooking}
            variant="contained"
            color="primary"
          >
            {t("Book now")}
          </Button>
        </SeeditDialogActions>
      </Dialog>
    </>
  );
};

export default TopList;
